// The caller's own supervision requests: the list (polled while one is
// pending), asking and withdrawing. When a request stops pending (approved and
// carried out, perhaps on another device), whatever it changed is refetched.
import type { Schemas } from "@10twenty/api";
import { useMutation, useQuery, useQueryClient, type QueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { api, unwrap } from "../api";
import { BUBBLES_KEY } from "../sharing/useSharingBubbles";

type Request = Schemas["SupervisionRequest"];

export const MY_REQUESTS_KEY = ["me", "supervision-requests"] as const;
/** Picks up guardians' decisions this often while a request is pending. */
const POLL_MS = 15_000;

/** Everything an approved request may have changed: sharing, bubbles, circles joined, left or created. */
export async function refreshAfterRequest(queryClient: QueryClient) {
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: ["me"] }),
    queryClient.invalidateQueries({ queryKey: ["circles"] }),
    queryClient.invalidateQueries({ queryKey: ["circle"] }),
    queryClient.invalidateQueries({ queryKey: BUBBLES_KEY }),
  ]);
}

export function useMyRequests(enabled = true) {
  const queryClient = useQueryClient();
  const query = useQuery({
    queryKey: MY_REQUESTS_KEY,
    queryFn: async () => unwrap(await api.GET("/me/supervision-requests")),
    enabled,
    refetchInterval: (q) => (q.state.data?.some((r) => r.status === "pending") ? POLL_MS : false),
  });
  const pending = (query.data ?? [])
    .filter((r) => r.status === "pending")
    .map((r) => r.id)
    .sort()
    .join();
  const seen = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (query.data === undefined) return;
    const before = seen.current;
    seen.current = pending;
    // One that was pending is decided now.
    if (before !== undefined && before.split(",").some((id) => id && !pending.split(",").includes(id))) {
      void refreshAfterRequest(queryClient);
    }
  }, [pending, query.data, queryClient]);
  return query;
}

/** Puts a request the server just returned at the top of the cached list (replacing an older copy). */
function remember(queryClient: QueryClient, request: Request) {
  queryClient.setQueryData<Request[]>(MY_REQUESTS_KEY, (list) => [
    request,
    ...(list ?? []).filter((r) => r.id !== request.id),
  ]);
}

export function useAsk() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (body: Schemas["SupervisionRequestCreate"]) =>
      unwrap(await api.POST("/me/supervision-requests", { body })),
    onSuccess: (request) => remember(queryClient, request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: MY_REQUESTS_KEY }),
  });
}

export function useWithdraw() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (requestId: string) =>
      unwrap(await api.DELETE("/me/supervision-requests/{requestId}", { params: { path: { requestId } } })),
    onSuccess: (request) => remember(queryClient, request),
    onSettled: () => queryClient.invalidateQueries({ queryKey: MY_REQUESTS_KEY }),
  });
}
